import { Component, Prop, h, Host, Build } from '@stencil/core';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

const COMPONENT_NAME = 'sn-hero-header-type-5';

interface HiddenField {
  name?: string;
  type?: string;
  value?: string;
}

/**
 * Two-column B2B integration hero header for SignNow integration/partner pages.
 * Left column: partner logo pair (greeting), H1 heading, description paragraph.
 * Right column: lead capture form card with form title and contact fields.
 * Differentiator: showcases integration partnership via logo pair; used on signNow integration/partner pages.
 *
 * @product signnow
 * @source hero-header
 * @version 1.0.0
 * @category SignNow
 * @contentConfig Two-column B2B integration hero header with logo greeting, heading, and lead capture form
 * @slot greeting - {multiple}, <me-picture> - Two partner logo boxes: first = signNow logo, second = partner logo
 * @slot heading - {required}, <me-heading tag="h1" variant="1"> - Main H1 section heading
 * @slot description - <me-text tag="p"> - Short description paragraph below the heading
 * @slot form-title - <me-text tag="p"> - Title displayed above the form fields
 * @slot form-notice - <me-text tag="p"> - Notice text below the form title (e.g. asterisk note)
 * @slot form - {required}, {multiple}, <me-input>, <me-button> - Lead form fields and submit button
 * @example
 *   <sn-hero-header-type-5
 *     action="/sales/esignature-demo-request"
 *     redirect-url="https://www.signnow.com/thankyou"
 *     agreement-note='By clicking "Request a demo" I agree to receive marketing communications from signNow in accordance with the <a href="https://legal.signnow.com/terms" target="_blank">Terms of Service</a> and <a href="https://legal.signnow.com/privacy-notice" target="_blank">Privacy Policy</a>'
 *     hidden-fields='[{"name":"industry_name","value":""},{"name":"_token","value":""},{"name":"gclid","value":""}]'
 *   >
 *     <me-picture slot="greeting" src-set="https://marketing-static.signnow.com/386/images/integrations/logo-signnow.svg" alt="signNow logo"></me-picture>
 *     <me-picture slot="greeting" src-set="https://marketing-static.signnow.com/288/images/integrations/urban/urban-logo@2x.png" alt="UrbanMix logo"></me-picture>
 *     <me-heading slot="heading" tag="h1" variant="1">airSlate SignNow for UrbanMix</me-heading>
 *     <me-text slot="description" tag="p">Eliminate tedious paperwork and accelerate approvals with the airSlate SignNow and UrbanMix integration.</me-text>
 *     <me-text slot="form-title" tag="p">Learn more about signNow integrations</me-text>
 *     <me-text slot="form-notice" tag="p">Asterisk denotes mandatory fields (*)</me-text>
 *     <div slot="form" class="sn-hero-header-type-5__form-row">
 *       <me-input name="first_name" placeholder="First Name" required required-message="Please enter your First Name"></me-input>
 *       <me-input name="last_name" placeholder="Last Name" required required-message="Please enter your Last Name"></me-input>
 *     </div>
 *     <me-input slot="form" name="email" type="email" placeholder="Business email" required required-message="Please enter your Business Email"></me-input>
 *     <me-input slot="form" name="phone" type="tel" placeholder="Phone number" required required-message="Please enter your Phone number"></me-input>
 *     <me-input slot="form" name="company_name" placeholder="Company name" required required-message="Please enter your Company name"></me-input>
 *     <me-button slot="form" tag="button" type="submit" variant="primary">Request a demo</me-button>
 *   </sn-hero-header-type-5>
 */
@Component({
  tag: 'sn-hero-header-type-5',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SNHeroHeaderType5 {
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * URL the lead form posts to. When omitted, the form has no action.
   */
  @Prop() action?: string;

  /**
   * URL the user is redirected to after a successful form submission. When omitted, no redirect occurs.
   */
  @Prop({ attribute: 'redirect-url' }) redirectUrl?: string;

  /**
   * Raw HTML for the consent/agreement note shown below the submit button
   * (e.g. `By clicking "Request a demo" I agree to ... <a href="https://legal.signnow.com/terms">Terms of Service</a> ...`).
   * Rendered directly by this component rather than as a slotted `<me-text raw-html>` child,
   * since slot-relocated hosts that render their own raw-html content (no `<slot>` to reconcile
   * against) get duplicated by the client during hydration of prerendered pages.
   */
  @Prop({ attribute: 'agreement-note' }) agreementNote?: string;

  /**
   * Extra hidden form fields the visual form doesn't expose. Accepts either a real array
   * (set via JS property, e.g. Storybook `.hidden-fields=` or a parent component's
   * `hiddenFields={array}`) or a JSON string of `{ "name": string, "value"?: string }`
   * objects (set via the `hidden-fields` HTML attribute / Herald). It is normalized to an
   * array in `componentWillLoad` and rendered as `<input type="hidden">` inside the lead
   * form so the fields post alongside the visible ones. The `| string` union is required so
   * Stencil observes the `hidden-fields` attribute.
   *
   * Used by the backend page migrator to inject values the SignNow landing expects
   * (e.g. `industry_name`, `_token`, `gclid`). Static per-page values (like
   * `industry_name`) are set here directly; per-request dynamic values (`_token`,
   * `gclid`) are populated by the backend/runtime — this repo only renders the fields.
   *
   * @example [{"name":"industry_name","value":"comparison"},{"name":"_token","value":""},{"name":"gclid","value":""}]
   * @type {array}
   * @hiddenFields.type {object}
   * @hiddenFields.property {string} name - Field name posted with the form (e.g. "gclid", "industry_name")
   * @hiddenFields.property {string} type - Native input type for the rendered field; defaults to "hidden"
   * @hiddenFields.property {string} value - Value posted for the field; may be empty (backend/runtime fills dynamic ones)
   * @hiddenFields.required name
   */
  @Prop({ mutable: true, attribute: 'hidden-fields' }) hiddenFields?: HiddenField[] | string;

  componentWillLoad() {
    // Normalize `hiddenFields`: accept a JSON string (HTML attribute / Herald, with
    // possible `&quot;` entities) or a real array (JS property). Malformed input → [].
    this.hiddenFields = parseJsonProp<HiddenField[]>(this.hiddenFields, []);
  }

  /**
   * Render the normalized `hiddenFields` as hidden `<input>` elements. Returns `null`
   * when there are none, so a malformed or empty value can never break the form.
   */
  private renderHiddenFields() {
    const fields = Array.isArray(this.hiddenFields) ? this.hiddenFields : [];
    const inputs = fields.filter(field => field && field.name);
    if (inputs.length === 0) return null;
    // Wrap in a single hidden container: scoped-slot relocation reliably moves a single
    // element into `form-handler-type-1`'s form, but leaves a bare array of inputs outside it.
    return (
      <div class={`${COMPONENT_NAME}__hidden-fields`} hidden>
        {inputs.map(field => (
          <input type={field.type || 'hidden'} name={field.name} value={field.value ?? ''} />
        ))}
      </div>
    );
  }

  /**
   * Redirect to the thank-you page after the lead form posts successfully.
   * `form-handler-type-1` only emits `formSubmitSuccess` once the POST returns `response.ok`.
   */
  private handleSubmitSuccess = () => {
    if (!Build.isBrowser || !this.redirectUrl) return;
    window.location.href = this.redirectUrl;
  };

  render() {
    return (
      <Host data-color-mode={this.colorMode}>
        <section class={COMPONENT_NAME} aria-label="Hero header">
          <div class={`${COMPONENT_NAME}__container`}>
            <div class={`${COMPONENT_NAME}__row`}>
              <div class={`${COMPONENT_NAME}__column ${COMPONENT_NAME}__column--details`}>
                <div class={`${COMPONENT_NAME}__greeting`}>
                  <slot name="greeting"></slot>
                </div>
                <slot name="heading"></slot>
                <slot name="description"></slot>
              </div>
              <div class={`${COMPONENT_NAME}__column ${COMPONENT_NAME}__column--form`}>
                <form-handler-type-1 class={`${COMPONENT_NAME}__form-wrapper`} disableFieldPrefix action={this.action} encoding="urlencoded" onFormSubmitSuccess={this.handleSubmitSuccess}>
                  <div class={`${COMPONENT_NAME}__form-title`}>
                    <slot name="form-title"></slot>
                  </div>
                  <div class={`${COMPONENT_NAME}__form-notice`}>
                    <slot name="form-notice"></slot>
                  </div>
                  <slot name="form"></slot>
                  {this.renderHiddenFields()}
                  {this.agreementNote && <p class={`${COMPONENT_NAME}__agreement-note`} innerHTML={this.agreementNote}></p>}
                </form-handler-type-1>
              </div>
            </div>
          </div>
        </section>
      </Host>
    );
  }
}
